import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { api } from '@/lib/api'
import { uploadToR2 } from '@/lib/upload'
import { useConfiguracion } from './hooks'

const METODOS = [
  { key: 'efectivo',         label: 'Efectivo' },
  { key: 'transferencia',    label: 'Transferencia' },
  { key: 'debito',           label: 'Débito' },
  { key: 'credito',          label: 'Crédito' },
  { key: 'cuenta_corriente', label: 'Cuenta corriente' },
]

interface FormState {
  nombre_negocio:       string
  telefono:             string
  direccion:            string
  alias_transferencia:  string
  cbu:                  string
  qr_transferencia_url: string
  info_adicional:       string
  mensaje_ticket:       string
  formato_ticket:       'a4' | 'termica_80mm'
  metodos_pago_activos: string[]
}

function blankForm(): FormState {
  return {
    nombre_negocio: '',
    telefono: '',
    direccion: '',
    alias_transferencia: '',
    cbu: '',
    qr_transferencia_url: '',
    info_adicional: '',
    mensaje_ticket: 'Gracias por su compra',
    formato_ticket: 'termica_80mm',
    metodos_pago_activos: ['efectivo', 'transferencia', 'debito', 'credito', 'cuenta_corriente'],
  }
}

export default function ConfiguracionPage() {
  const qc = useQueryClient()
  const { data: config, isLoading, error: loadError } = useConfiguracion()
  const [form, setForm] = useState<FormState>(blankForm())
  const [currentPass, setCurrentPass] = useState('')
  const [nuevaPass, setNuevaPass] = useState('')
  const [confirmarPass, setConfirmarPass] = useState('')
  const [cambiandoPass, setCambiandoPass] = useState(false)
  const qrInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!config) return
    setForm({
      nombre_negocio:     config.nombre_negocio ?? '',
      telefono:           config.telefono ?? '',
      direccion:          config.direccion ?? '',
      alias_transferencia:  config.alias_transferencia ?? '',
      cbu:                  config.cbu ?? '',
      qr_transferencia_url: config.qr_transferencia_url ?? '',
      info_adicional:       config.info_adicional ?? '',
      mensaje_ticket:       config.mensaje_ticket ?? 'Gracias por su compra',
      formato_ticket:       (config.formato_ticket as 'a4' | 'termica_80mm') ?? 'termica_80mm',
      metodos_pago_activos: config.metodos_pago_activos ?? [],
    })
  }, [config])

  const guardar = useMutation({
    mutationFn: () => api.put('/api/configuracion', form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['configuracion'] })
      toast.success('Configuración guardada')
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error al guardar'
      toast.error(msg, { duration: 5000 })
    },
  })

  function set<K extends keyof FormState>(k: K) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))
  }

  async function handleCambiarPassword() {
    if (!currentPass) { toast.error('Ingresá tu contraseña actual'); return }
    if (nuevaPass.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return }
    if (nuevaPass !== confirmarPass) { toast.error('Las contraseñas no coinciden'); return }
    setCambiandoPass(true)
    try {
      await api.put('/api/auth/password', { currentPassword: currentPass, newPassword: nuevaPass })
      toast.success('Contraseña actualizada')
      setCurrentPass('')
      setNuevaPass('')
      setConfirmarPass('')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al cambiar contraseña')
    } finally {
      setCambiandoPass(false)
    }
  }

  async function handleQRUpload(file: File) {
    try {
      const url = await uploadToR2(file, 'qr')
      setForm(f => ({ ...f, qr_transferencia_url: url }))
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al subir QR')
    }
  }

  function toggleMetodo(key: string) {
    setForm(f => ({
      ...f,
      metodos_pago_activos: f.metodos_pago_activos.includes(key)
        ? f.metodos_pago_activos.filter(m => m !== key)
        : [...f.metodos_pago_activos, key],
    }))
  }

  if (isLoading) {
    return (
      <div style={{ padding: 32, color: 'var(--me-text-3)', fontSize: 13.5 }}>
        Cargando configuración…
      </div>
    )
  }

  if (loadError) {
    return (
      <div style={{ maxWidth: 680 }}>
        <div style={{ marginBottom: 24 }}>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Administración</div>
          <h1 className="me-page-title">Configuración</h1>
        </div>
        <div style={{
          padding: '16px 20px', borderRadius: 12,
          background: 'var(--me-bad-soft)',
          border: '1px solid rgba(239,68,68,0.2)',
        }}>
          <div style={{ fontWeight: 600, color: 'var(--me-bad)', marginBottom: 4 }}>
            Error al cargar la configuración
          </div>
          <div style={{ fontSize: 13, color: 'var(--me-text-2)', marginBottom: 8 }}>
            {loadError.message}
          </div>
          <div style={{ fontSize: 12, color: 'var(--me-text-3)' }}>
            Probable causa: la migración 003_configuracion.sql no fue aplicada todavía.
            Ejecutala en el SQL Editor de Supabase y recargá la página.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div className="me-eyebrow" style={{ marginBottom: 4 }}>Administración</div>
        <h1 className="me-page-title">Configuración</h1>
      </div>

      {/* Datos del negocio */}
      <div className="me-card" style={{ marginBottom: 20 }}>
        <h2 className="me-section-title" style={{ marginBottom: 16 }}>Datos del negocio</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              Nombre del negocio
            </label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              value={form.nombre_negocio}
              onChange={set('nombre_negocio')}
              placeholder="Ej: Almacén El Progreso"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
                Teléfono
              </label>
              <input
                className="me-input"
                style={{ width: '100%' }}
                value={form.telefono}
                onChange={set('telefono')}
                placeholder="Ej: 011 1234-5678"
              />
            </div>
            <div>
              <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
                Dirección
              </label>
              <input
                className="me-input"
                style={{ width: '100%' }}
                value={form.direccion}
                onChange={set('direccion')}
                placeholder="Ej: Av. San Martín 1234"
              />
            </div>
          </div>

          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              Información adicional
            </label>
            <textarea
              className="me-input"
              style={{ width: '100%', minHeight: 72, resize: 'vertical', lineHeight: 1.5 }}
              value={form.info_adicional}
              onChange={set('info_adicional')}
              placeholder="Horarios de atención, condiciones de venta, notas…"
            />
          </div>
        </div>
      </div>

      {/* Datos bancarios */}
      <div className="me-card" style={{ marginBottom: 20 }}>
        <h2 className="me-section-title" style={{ marginBottom: 4 }}>Datos bancarios</h2>
        <p style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 16, marginTop: 4 }}>
          Se usan en mensajes de WhatsApp y comprobantes de transferencia.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              Alias
            </label>
            <input
              className="me-input"
              style={{ width: '100%', fontFamily: 'var(--me-mono)' }}
              value={form.alias_transferencia}
              onChange={set('alias_transferencia')}
              placeholder="Ej: almacen.progreso.mp"
            />
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              CBU
            </label>
            <input
              className="me-input"
              style={{ width: '100%', fontFamily: 'var(--me-mono)', letterSpacing: '0.04em' }}
              value={form.cbu}
              onChange={set('cbu')}
              placeholder="22 dígitos"
              maxLength={22}
            />
          </div>

          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              QR de pago (Mercado Pago u otro)
            </label>
            <input
              ref={qrInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleQRUpload(f) }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {form.qr_transferencia_url ? (
                <img
                  src={form.qr_transferencia_url}
                  alt="QR de transferencia"
                  style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--me-line)', background: '#fff' }}
                />
              ) : (
                <div style={{
                  width: 80, height: 80, borderRadius: 8,
                  border: '1.5px dashed var(--me-line)',
                  background: 'var(--me-surface-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, color: 'var(--me-text-4)', textAlign: 'center', padding: 8,
                }}>
                  Sin QR
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  type="button"
                  className="me-btn me-btn-ghost"
                  style={{ fontSize: 12, padding: '5px 12px' }}
                  onClick={() => qrInputRef.current?.click()}
                >
                  {form.qr_transferencia_url ? 'Cambiar QR' : 'Subir QR'}
                </button>
                {form.qr_transferencia_url && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--me-bad)', padding: 0, textAlign: 'left' }}
                    onClick={() => setForm(f => ({ ...f, qr_transferencia_url: '' }))}
                  >
                    Quitar QR
                  </button>
                )}
                <span style={{ fontSize: 11, color: 'var(--me-text-4)' }}>
                  O pegá la URL de la imagen directamente:
                </span>
                <input
                  className="me-input"
                  style={{ fontSize: 12 }}
                  type="url"
                  value={form.qr_transferencia_url}
                  onChange={set('qr_transferencia_url')}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métodos de pago */}
      <div className="me-card" style={{ marginBottom: 24 }}>
        <h2 className="me-section-title" style={{ marginBottom: 4 }}>Métodos de pago</h2>
        <p style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 16, marginTop: 4 }}>
          Solo los métodos activos aparecen disponibles al registrar una venta.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {METODOS.map(m => {
            const on = form.metodos_pago_activos.includes(m.key)
            return (
              <label
                key={m.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${on ? 'var(--me-acid)' : 'var(--me-line)'}`,
                  background: on ? 'var(--me-acid-soft)' : 'transparent',
                  transition: 'all var(--me-dur-short)',
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleMetodo(m.key)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#c9f000' }}
                />
                <span style={{
                  fontSize: 13.5, fontWeight: 500,
                  color: on ? '#3a4800' : 'var(--me-text)',
                }}>
                  {m.label}
                </span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Ticket */}
      <div className="me-card" style={{ marginBottom: 20 }}>
        <h2 className="me-section-title" style={{ marginBottom: 4 }}>Ticket de venta</h2>
        <p style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 16, marginTop: 4 }}>
          Configurá el formato y el mensaje que aparece al pie del comprobante.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Formato */}
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Formato de impresión</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {([
                { key: 'termica_80mm', label: 'Térmica 80mm', desc: 'Impresora de tickets' },
                { key: 'a4',          label: 'Hoja A4',       desc: 'Impresora de escritorio' },
              ] as const).map(f => {
                const on = form.formato_ticket === f.key
                return (
                  <label
                    key={f.key}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `1.5px solid ${on ? 'var(--me-acid)' : 'var(--me-line)'}`,
                      background: on ? 'var(--me-acid-soft)' : 'transparent',
                      transition: 'all var(--me-dur-short)',
                    }}
                  >
                    <input
                      type="radio"
                      name="formato_ticket"
                      checked={on}
                      onChange={() => setForm(f2 => ({ ...f2, formato_ticket: f.key }))}
                      style={{ marginTop: 2, accentColor: '#c9f000' }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: on ? '#3a4800' : 'var(--me-text)' }}>
                        {f.label}
                      </div>
                      <div style={{ fontSize: 11, color: on ? '#4a5800' : 'var(--me-text-3)', marginTop: 2 }}>
                        {f.desc}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Mensaje pie */}
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              Mensaje al pie del ticket
            </label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              value={form.mensaje_ticket}
              onChange={set('mensaje_ticket')}
              placeholder="Ej: Gracias por su compra"
            />
          </div>
        </div>
      </div>

      {/* Seguridad */}
      <div className="me-card" style={{ marginBottom: 24 }}>
        <h2 className="me-section-title" style={{ marginBottom: 4 }}>Seguridad</h2>
        <p style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 16, marginTop: 4 }}>
          Cambiá tu contraseña de acceso al sistema.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Contraseña actual</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="password"
              value={currentPass}
              onChange={e => setCurrentPass(e.target.value)}
              placeholder="Tu contraseña actual"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Nueva contraseña</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="password"
              value={nuevaPass}
              onChange={e => setNuevaPass(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Confirmar contraseña</label>
            <input
              className="me-input"
              style={{ width: '100%' }}
              type="password"
              value={confirmarPass}
              onChange={e => setConfirmarPass(e.target.value)}
              placeholder="Repetir contraseña"
              autoComplete="new-password"
            />
          </div>
          <button
            className="me-btn me-btn-ghost"
            onClick={handleCambiarPassword}
            disabled={cambiandoPass || !currentPass || !nuevaPass || !confirmarPass}
            style={{ alignSelf: 'flex-start' }}
          >
            {cambiandoPass ? 'Cambiando…' : 'Cambiar contraseña'}
          </button>
        </div>
      </div>

      <button
        className="me-btn me-btn-primary"
        style={{ width: '100%' }}
        onClick={() => guardar.mutate()}
        disabled={guardar.isPending}
      >
        {guardar.isPending ? 'Guardando…' : 'Guardar configuración'}
      </button>
    </div>
  )
}
